import React, {useRef, useState} from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragCancelEvent,
  CollisionDetection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
} from '@dnd-kit/core'
import Swal from 'sweetalert2'
import {toast} from 'react-toastify'
import {finishActions, realiserAction} from '../../../../../components/requests'
import KanbanColumn from './KanbanColumn'
import CardItem from '../CardItem'
import {Action, ViewType} from '../../MesActionsListPage'
import {ModeleMesActions} from '../../../../../components/Models'
import {shouldDisplayFonctionnalite} from '../../../../../components/helpers'

type KanbanStatusCode = 'NOUVELLE' | 'ENCOURS' | 'TERMINEE'

const isKanbanStatusCode = (
  status: string | undefined
): status is KanbanStatusCode => {
  return (
    status === 'NOUVELLE' ||
    status === 'ENCOURS' ||
    status === 'TERMINEE'
  )
}

type KanbanStatus = {
  libelle: string
  code: KanbanStatusCode
}

type DragState = {
  action: Action
  initialStatus: KanbanStatusCode
}

type KanbanViewProps = {
  actions: Action[]
  currentUser: any
  view: ViewType
  setActions: React.Dispatch<React.SetStateAction<Action[]>>
  updateActionEverywhere: (action: Action) => void
  removeActionEverywhere: (actionId: number | string) => void
  filters?: any
}

const statuts: KanbanStatus[] = [
  {
    libelle: 'A faire',
    code: 'NOUVELLE',
  },
  {
    libelle: 'En cours',
    code: 'ENCOURS',
  },
  {
    libelle: 'Réalisée',
    code: 'TERMINEE',
  },
]

const kanbanCollisionDetectionStrategy: CollisionDetection = (
  args
) => {
  return pointerWithin(args)
}

const KanbanView = ({
                      actions,
                      currentUser,
                      view,
                      filters,
                      setActions,
                      updateActionEverywhere,
                      removeActionEverywhere,
                    }: KanbanViewProps) => {
  const draggedAction = useRef<DragState | null>(null)
  const [activeAction, setActiveAction] = useState<Action | null>(null)
  const [previewStatus, setPreviewStatus] = useState<KanbanStatusCode | null>(null)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const dropTargetId = useRef<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const getColumnFromId = (
    id: string | number
  ): KanbanStatusCode | undefined => {
    const value = String(id)

    const status = statuts.find(
      (item) => item.code === value
    )

    return status?.code
  }

  const restoreAction = () => {
    const dragState =
      draggedAction.current

    if (!dragState) {
      return
    }

    const originalAction =
      dragState.action

    setActions((prevActions) => {
      const exists = prevActions.some(
        (item) =>
          String(item.id) ===
          String(originalAction.id)
      )

      if (exists) {
        return prevActions.map((item) =>
          String(item.id) ===
          String(originalAction.id)
            ? originalAction
            : item
        )
      }

      return [
        ...prevActions,
        originalAction,
      ]
    })
  }

  const clearPreview = () => {
    setPreviewStatus(null)
    setPreviewIndex(null)
    dropTargetId.current = null
  }

  const handleDragStart = (
    event: DragStartEvent
  ) => {
    const {active} = event

    const action = actions.find(
      (item) =>
        String(item.id) ===
        String(active.id)
    )

    if (!action) {
      draggedAction.current = null
      setActiveAction(null)
      return
    }

    const initialStatus =
      action.statut?.code

    if (
      !isKanbanStatusCode(initialStatus) ||
      !shouldDisplayFonctionnalite(currentUser, 'TRAITER ACTION')
    ) {
      draggedAction.current = null
      setActiveAction(null)
      return
    }

    const dragState: DragState = {
      action: {
        ...action,
        statut: action.statut
          ? {...action.statut}
          : action.statut,
      },
      initialStatus,
    }

    draggedAction.current = dragState

    setActiveAction(
      dragState.action
    )

    clearPreview()

    setActions((prevActions) =>
      prevActions.filter(
        (item) =>
          String(item.id) !==
          String(active.id)
      )
    )
  }

  const handleDragOver = (
    event: DragOverEvent
  ) => {
    const {over} = event

    if (!over) {
      clearPreview()
      return
    }

    const overId =
      String(over.id)

    dropTargetId.current =
      overId

    const columnStatus =
      getColumnFromId(overId)

    if (columnStatus) {
      const destinationItems =
        actions.filter(
          (item) =>
            item.statut?.code ===
            columnStatus
        )

      setPreviewStatus(
        columnStatus
      )

      setPreviewIndex(
        destinationItems.length
      )

      return
    }

    const targetAction =
      actions.find(
        (item) =>
          String(item.id) ===
          overId
      )

    if (!targetAction) {
      clearPreview()
      return
    }

    const targetStatus =
      targetAction.statut?.code

    if (
      targetStatus !== 'NOUVELLE' &&
      targetStatus !== 'ENCOURS' &&
      targetStatus !== 'TERMINEE'
    ) {
      clearPreview()
      return
    }

    const destinationItems =
      actions.filter(
        (item) =>
          item.statut?.code ===
          targetStatus
      )

    const targetIndex =
      destinationItems.findIndex(
        (item) =>
          String(item.id) ===
          overId
      )

    if (targetIndex === -1) {
      clearPreview()
      return
    }

    setPreviewStatus(
      targetStatus
    )

    setPreviewIndex(
      targetIndex
    )
  }

  /**
   * Construit l'action avec son nouveau statut.
   */
  const buildUpdatedAction = (
    action: Action,
    targetStatusCode: KanbanStatusCode
  ): Action => {
    return {
      ...action,
      statut: {
        ...action.statut,
        code: targetStatusCode,
        libelle:
          statuts.find(
            (status) =>
              status.code ===
              targetStatusCode
          )?.libelle ??
          action.statut?.libelle,
      },
    }
  }

  //Insère l'action immédiatement dans actions à la position souhaitée.
  const insertActionAtDropPosition = (
    action: Action,
    targetStatusCode: KanbanStatusCode,
    targetIndex: number
  ) => {
    const updatedAction =
      buildUpdatedAction(
        action,
        targetStatusCode
      )

    setActions((prevActions) => {
      const destinationItems =
        prevActions.filter(
          (item) =>
            item.statut?.code ===
            targetStatusCode
        )

      const otherItems =
        prevActions.filter(
          (item) =>
            item.statut?.code !==
            targetStatusCode
        )

      const safeIndex = Math.max(
        0,
        Math.min(
          targetIndex,
          destinationItems.length
        )
      )

      destinationItems.splice(
        safeIndex,
        0,
        updatedAction
      )

      return [
        ...otherItems,
        ...destinationItems,
      ]
    })

    return updatedAction
  }

  const updateActionStatus = async (
    action: Action,
    targetStatusCode: KanbanStatusCode
  ) => {
    const dragState =
      draggedAction.current

    if (!dragState) {
      return
    }

    if (
      dragState.initialStatus ===
      targetStatusCode
    ) {
      draggedAction.current = null
      setActiveAction(null)
      return
    }

    const actionId =
      Number(action.id)

    if (!Number.isFinite(actionId)) {
      restoreAction()

      draggedAction.current = null
      setActiveAction(null)

      toast.error(
        "Identifiant de l'action invalide"
      )

      return
    }

    try {
      const res =
        await realiserAction(
          actionId,
          targetStatusCode
        )

      if (
        res?.status === 200 &&
        res.data
      ) {
        setActions(
          (prevActions) =>
            prevActions.map(
              (item) =>
                String(item.id) ===
                String(res.data.id)
                  ? {
                    ...item,
                    ...res.data,
                    statut:
                    res.data.statut,
                  }
                  : item
            )
        )

        const updatedAction: Action = {
          ...action,
          ...res.data,
          statut:
          res.data.statut,
        }

        updateActionEverywhere(
          updatedAction
        )

        draggedAction.current = null
        setActiveAction(null)

        return
      }

      restoreAction()

      draggedAction.current = null
      setActiveAction(null)
    } catch (error) {
      console.error(
        'Erreur lors du changement de statut',
        error
      )

      restoreAction()

      draggedAction.current = null
      setActiveAction(null)
    }
  }

  const buildActionForFinish = (
    action: Action
  ): ModeleMesActions => {
    const actionId =
      Number(action.id)

    const clientId =
      action.client?.id !== undefined
        ? Number(action.client.id)
        : null

    const demandeId =
      action.demande?.id !== undefined
        ? Number(action.demande.id)
        : null

    return {
      id: Number.isFinite(actionId)
        ? actionId
        : undefined,
      titre: action.titre ?? null,
      type: action.type?.code ?? null,
      statut: action.statut?.code ?? null,
      origine: action.origine ?? null,
      dateEcheance: action.dateEcheance ?? null,
      commentaire: action.commentaire ?? null,
      prioritaire: action.prioritaire ?? false,
      clientId:
        clientId !== null && Number.isFinite(clientId)
          ? clientId
          : null,
      demandeId:
        demandeId !== null && Number.isFinite(demandeId)
          ? demandeId
          : null,
      lu: action.lu ?? null,
    }
  }

  const confirmRealisation = (action: Action) => {
    Swal.fire({
      title: 'Attention',
      text:
        'Etes-vous sûr de vouloir valider la réalisation de cette action ? Son statut passera en "Réalisé" et la demande retournera dans son flux de suivi classique',
      icon: 'warning',
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonColor: '#1BC5BD',
      cancelButtonColor: '#F64E60',
    }).then(async (alertResult) => {
      if (!alertResult.isConfirmed) {
        restoreAction()
        draggedAction.current = null
        setActiveAction(null)

        return
      }

      const idToast = toast.loading(
        'Traitement en cours...'
      )

      try {
        const actionId = Number(action.id)

        if (!Number.isFinite(actionId)) {
          restoreAction()

          draggedAction.current = null
          setActiveAction(null)

          toast.update(idToast, {
            render: "Identifiant de l'action invalide",
            type: 'error',
            isLoading: false,
            autoClose: 2000,
          })

          return
        }

        const actionForFinish =
          buildActionForFinish(action)

        const res = await finishActions(
          actionForFinish
        )

        if (
          res?.status === 200 &&
          res.data
        ) {
          setActions(
            (prevActions) =>
              prevActions.map(
                (item) =>
                  String(item.id) ===
                  String(
                    res.data.id
                  )
                    ? {
                      ...item,
                      ...res.data,
                      statut:
                      res.data
                        .statut,
                    }
                    : item
              )
          )

          const updatedAction:
            Action = {
            ...action,
            ...res.data,
            statut:
            res.data.statut,
          }

          updateActionEverywhere(
            updatedAction
          )

          toast.update(
            idToast,
            {
              render:
                'Action réalisée',
              type: 'success',
              isLoading: false,
              autoClose: 2000,
            }
          )

          draggedAction.current =
            null

          setActiveAction(null)

          return
        }

        restoreAction()

        draggedAction.current = null
        setActiveAction(null)

        toast.update(
          idToast,
          {
            render:
              'Erreur lors de la réalisation',
            type: 'error',
            isLoading: false,
            autoClose: 2000,
          }
        )
      } catch (error) {
        console.error(
          'Erreur lors de la réalisation',
          error
        )

        restoreAction()

        draggedAction.current = null
        setActiveAction(null)

        toast.update(
          idToast,
          {
            render:
              'Erreur lors de la réalisation',
            type: 'error',
            isLoading: false,
            autoClose: 2000,
          }
        )
      }
    })
  }

  const handleDragEnd = (
    event: DragEndEvent
  ) => {
    const dragState =
      draggedAction.current

    if (!dragState) {
      setActiveAction(null)
      return
    }

    const action =
      dragState.action

    if (
      previewStatus === null ||
      previewIndex === null
    ) {
      restoreAction()

      draggedAction.current = null
      setActiveAction(null)
      clearPreview()

      return
    }

    const targetStatusCode =
      previewStatus

    if (
      dragState.initialStatus ===
      targetStatusCode
    ) {
      restoreAction()

      draggedAction.current = null
      setActiveAction(null)
      clearPreview()

      return
    }

    if (
      targetStatusCode ===
      'TERMINEE'
    ) {
      const updatedAction = insertActionAtDropPosition(action, targetStatusCode, previewIndex)
      setActiveAction(null)
      clearPreview()
      confirmRealisation(updatedAction)

      return
    }

    const updatedAction =
      insertActionAtDropPosition(
        action,
        targetStatusCode,
        previewIndex
      )

    setActiveAction(null)

    clearPreview()

    updateActionStatus(updatedAction, targetStatusCode)
  }

  const handleDragCancel = (_event: DragCancelEvent) => {
    restoreAction()

    draggedAction.current = null
    setActiveAction(null)

    clearPreview()
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={kanbanCollisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className='d-flex gap-6 px-4 justify-content-between'>
        {statuts.map((status) => {
          const columnItems =
            actions.filter(
              (item) =>
                item?.statut?.code ===
                status.code
            )

          const isPreviewColumn =
            previewStatus === status.code &&
            previewIndex !== null &&
            activeAction !== null

          return (
            <KanbanColumn
              key={status.code}
              id={status.code}
              title={status.libelle}
              items={columnItems}
              view={view}
              currentUser={currentUser}
              setActions={setActions}
              showPlaceholder={
                isPreviewColumn
              }
              placeholderIndex={
                isPreviewColumn
                  ? previewIndex
                  : null
              }
              filters={filters}
            />
          )
        })}
      </div>
      <DragOverlay>
        {activeAction ? (
          <CardItem
            action={activeAction}
            currentUser={
              currentUser
            }
            view={view}
            setActions={
              setActions
            }
            filters={filters}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default KanbanView
